import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useRef } from "react"
import { motion } from "motion/react"
import SiteFooter from "./SiteFooter"
import {
    HomeIcon,
    HomeIconFocused,
    ProfileIcon,
    ProfileIconFocused,
    PromotionsIcon,
    PromotionsIconFocused,
    TicketsIcon,
    TicketsIconFocused,
    TicketsNotify,
    TicketsNotifyFocused,
} from "../../components/svgCollection"
import { unpaidOrderStore } from "../../store/unpaidOrderStore"
import { userStore } from "../../store/userStore"
import { afishaHref } from "../../lib/afishaTabs"
import { useCinemaHref } from "../../hooks/useCinemaHref"

const MONO = "'JetBrains Mono', monospace"
const RELOAD_ARM_MS = 2500

function isNavItemActive(currentPath: string, to: string, end: boolean) {
    if (to.includes("?")) return currentPath === to
    const pathname = currentPath.split("?")[0]
    if (end) return currentPath === to
    return pathname === to || pathname.startsWith(`${to}/`)
}

function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" })
    document.documentElement.scrollTo({ top: 0, behavior: "smooth" })
    document.body.scrollTo({ top: 0, behavior: "smooth" })
}

export default function Footer() {
    const { pathname, search } = useLocation()
    const navigate = useNavigate()
    const href = useCinemaHref()
    const afishaTo = href("/afisha")
    const hasUnpaidNotice = unpaidOrderStore(state => state.hasUnreadUnpaid())
    const { user } = userStore()
    const isStartPage = pathname === "/"
    const currentPath = `${pathname}${search}`
    const reloadPendingRef = useRef<string | null>(null)
    const reloadResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        reloadPendingRef.current = null
        if (reloadResetTimerRef.current) {
            clearTimeout(reloadResetTimerRef.current)
            reloadResetTimerRef.current = null
        }
    }, [pathname])

    const handleActiveNavTap = (to: string) => {
        if (reloadPendingRef.current === to) {
            if (reloadResetTimerRef.current) clearTimeout(reloadResetTimerRef.current)
            reloadPendingRef.current = null
            window.location.reload()
            return
        }

        reloadPendingRef.current = to
        scrollToTop()

        if (reloadResetTimerRef.current) clearTimeout(reloadResetTimerRef.current)
        reloadResetTimerRef.current = setTimeout(() => {
            reloadPendingRef.current = null
            reloadResetTimerRef.current = null
        }, RELOAD_ARM_MS)
    }

    const handleNavTap = (to: string) => {
        // Открыт вложенный экран раздела (например, /profile/notifications) —
        // тап по активному пункту возвращает на корень раздела
        if (currentPath !== to) {
            navigate(to)
            return
        }
        handleActiveNavTap(to)
    }

    const navItems = [
        {
            to: afishaTo,
            label: "Главная",
            icon: <HomeIcon />,
            focusedIcon: <HomeIconFocused />,
        },
        {
            to: afishaHref("Подборки"),
            label: "Подборки",
            icon: <PromotionsIcon />,
            focusedIcon: <PromotionsIconFocused />,
        },
        {
            to: afishaHref("Акции"),
            label: "Акции",
            icon: <PromotionsIcon />,
            focusedIcon: <PromotionsIconFocused />,
        },
        ...(user ? [{
            to: "/tickets",
            label: "Билеты",
            icon: hasUnpaidNotice ? <TicketsNotify /> : <TicketsIcon />,
            focusedIcon: hasUnpaidNotice ? <TicketsNotifyFocused /> : <TicketsIconFocused />,
        }] : []),
        {
            to: "/profile",
            label: "Профиль",
            icon: <ProfileIcon />,
            focusedIcon: <ProfileIconFocused />,
        },
    ]

    return (
        <footer>
            {!isStartPage && <SiteFooter />}

            {/* Mobile bottom nav — floating pill (на лендинге не показываем) */}
            {!isStartPage && (
                <nav
                    className="fixed z-10 flex items-center rounded-[28px] py-2 md:hidden"
                    style={{
                        left: 16,
                        right: "calc(16px + var(--scrollbar-w, 0px))",
                        bottom: "max(10px, var(--safe-bottom))",
                        background: "rgba(20, 35, 70, 0.82)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        backdropFilter: "blur(24px)",
                        WebkitBackdropFilter: "blur(24px)",
                        boxShadow: "0 -4px 16px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.2), 0 1px 0 rgba(255,255,255,0.06) inset",
                    }}>
                    {navItems.map(({ to, label, icon, focusedIcon }) => {
                        const end = to === afishaTo
                        const isActive = isNavItemActive(currentPath, to, end)

                        return (
                            <button
                                key={to}
                                type="button"
                                aria-current={isActive ? "page" : undefined}
                                className="flex-1 cursor-pointer border-0 bg-transparent p-0"
                                onClick={() => handleNavTap(to)}>
                                <motion.div
                                    whileTap={{ scale: 0.88 }}
                                    className="relative mx-1 flex flex-col items-center gap-1 py-1.5">
                                    <div
                                        className="absolute inset-0 rounded-[20px]"
                                        style={{
                                            background: "rgba(52,155,230,0.16)",
                                            border: "1px solid rgba(52,155,230,0.28)",
                                            opacity: isActive ? 1 : 0,
                                            transition: "opacity 0.18s ease",
                                        }}
                                    />
                                    <div className="relative h-6 w-6">
                                        {isActive ? focusedIcon : icon}
                                    </div>
                                    <span
                                        className="relative text-[10px]"
                                        style={{
                                            fontFamily: MONO,
                                            fontWeight: 700,
                                            color: isActive ? "#349be6" : "rgba(255,255,255,0.45)",
                                            letterSpacing: "0.04em",
                                            transition: "color 0.18s ease",
                                        }}>
                                        {label}
                                    </span>
                                </motion.div>
                            </button>
                        )
                    })}
                </nav>
            )}
        </footer>
    )
}
